import moment from 'moment';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  DeliveryCardStatus,
  IhutActivity,
  IhutActivityApi,
  IhutAvailableActivities,
  IhutAvailableActivitiesApi,
  IhutSurvey,
  IhutSurveyType,
  PanelistStatusPayload,
  getIhutAvailableActivitiesApi,
  getIhutProjectDetailsApi,
  getSupportRequest,
  updatePanelistStates
} from '../../api/ihut.ts';
import { ApplicationState, Slices } from '../types.ts';
import { isAfterCurrentDate } from '../../utils/date.ts';
import { API_STATUS_CODE } from '../../constants/common.js';

export type IhutState = {
  availableActivities: IhutAvailableActivities[];
  selectedActivity?: IhutActivity;
  loading: boolean;
  supportRequest?: {
    createdAt: string;
    updatedAt: string;
  };
  supportLoading: boolean;
  updateStateLoading: boolean;
};

const initialState: IhutState = {
  availableActivities: [],
  loading: false,
  supportLoading: false,
  updateStateLoading: false
};

export const actionPrefix = {
  getIhutAvailableActivities: 'ihut/getAvailableActivities',
  getIhutProjectDetails: 'ihut/getIhutProjectDetails',
  getIhutSupportRequest: 'ihut/getIhutSupportRequest',
  updatePanelistState: 'ihut/updatePanelistState'
};

const findSurvey = (
  activity: IhutActivityApi,
  type: IhutSurveyType = 'FEEDBACK'
) => activity.surveys.find((survey) => survey.type === type);

type GetStepStatusParams = {
  shipmentReceived: boolean;
  testingCompleted: boolean;
  feedbackSurvey?: IhutSurvey;
  isFeedbackSurveyTaken?: boolean;
};

export const getStepStatus = (
  {
    shipmentReceived,
    testingCompleted,
    feedbackSurvey,
    isFeedbackSurveyTaken = false
  }: GetStepStatusParams,
  isSurveyDataPresent = false
) => {
  if (!shipmentReceived) return 0;

  if (isFeedbackSurveyTaken) return 3;

  if (
    isSurveyDataPresent &&
    (!feedbackSurvey?.startTime || isAfterCurrentDate(feedbackSurvey.startTime))
  )
    return 1;

  if (testingCompleted) return 2;

  return 1;
};

const getDeliveryStatus = (status: string): DeliveryCardStatus => {
  if (status === 'DELIVERED') return 'delivered';
  return 'on-the-way';
};

const getSupportStatus = (activity: IhutActivityApi) => {
  if (activity.supportRequested) return 'in-support';
  if (activity.shippingDetails.received) return 'confirmed';
  return 'delivered';
};

const mapApiIhutActivitiesToUiIhutActivities = (
  activities: IhutAvailableActivitiesApi[]
): IhutAvailableActivities[] =>
  activities.map((currActivity) => ({
    ...currActivity,
    name: currActivity.projectName,
    stepStatus: getStepStatus(currActivity),
    completeBy: moment(currActivity.createdAt)
      .add(moment.duration(currActivity.testingDuration))
      .toISOString()
  }));

const mapApiIhutDetailsToUiIhutDetails = (
  activity: IhutActivityApi
): IhutActivity => {
  const isFeedbackSurveyTaken = activity.surveys.some(
    (currAct) =>
      currAct.type === 'FEEDBACK' &&
      (currAct.status === 'QUALIFIED' || currAct.status === 'TERMINATED')
  );
  return {
    ...activity,
    stepStatus: getStepStatus(
      {
        shipmentReceived: activity.shippingDetails.received,
        testingCompleted: activity.testingCompleted,
        feedbackSurvey: findSurvey(activity),
        isFeedbackSurveyTaken
      },
      true
    ),
    deliveryCardStatus: getDeliveryStatus(activity.shippingDetails.status),
    isFeedbackSurveyTaken,
    supportCardStatus: getSupportStatus(activity),
    deliveryConfirmedOn: activity.shippingDetails.deliveredOn,
    survey: findSurvey(activity) || findSurvey(activity, 'SCREENER')
  };
};

const getIhutAvailableActivities = createAsyncThunk(
  actionPrefix.getIhutAvailableActivities,
  (_, { rejectWithValue }) =>
    getIhutAvailableActivitiesApi().then((response) => {
      if (response.status === API_STATUS_CODE.SUCCESS) {
        return response.data;
      } else {
        return rejectWithValue(response);
      }
    })
);

const getIhutProjectDetails = createAsyncThunk(
  actionPrefix.getIhutProjectDetails,
  (projectId: string, { rejectWithValue }) =>
    getIhutProjectDetailsApi(projectId).then((response) => {
      if (response.status === API_STATUS_CODE.SUCCESS) {
        return response.data;
      } else {
        return rejectWithValue(response);
      }
    })
);

const getIhutSupportRequest = createAsyncThunk(
  actionPrefix.getIhutSupportRequest,
  (_, { getState, rejectWithValue }) => {
    const state = getState() as ApplicationState;
    const panelistId = localStorage.getItem('panelistId');
    if (!state.ihut.selectedActivity || !panelistId)
      return rejectWithValue('No selected activity or panelistId');
    const activity = state.ihut.selectedActivity;
    if (!activity.ihutProjectId)
      return rejectWithValue('Activity shortId not available');

    return getSupportRequest({
      shortId: activity.ihutProjectId,
      panelistId
    }).then((response) => {
      if (response.status === API_STATUS_CODE.SUCCESS) {
        return response.data;
      } else {
        return rejectWithValue(response);
      }
    });
  }
);

const updatePanelistState = createAsyncThunk(
  actionPrefix.updatePanelistState,
  (data: PanelistStatusPayload, { getState, rejectWithValue }) => {
    const state = getState() as ApplicationState;
    const activity = state.ihut.selectedActivity;
    if (!activity?.survey)
      return rejectWithValue('No selected activity or survey found');

    return updatePanelistStates({
      params: {
        surveyId: activity.survey.id,
        outBoundShippingId: activity.shippingDetails.outBoundShippingId,
        shortId: activity.ihutProjectId
      },
      status: data
    }).then((response) => {
      if (response.status === API_STATUS_CODE.NO_CONTENT) {
        return response;
      } else {
        return rejectWithValue(response);
      }
    });
  }
);

export const ihutSlice = createSlice({
  name: Slices.IHUT,
  initialState,
  reducers: {
    setDeliveryConfirmed: (state) => {
      if (state.selectedActivity) {
        state.selectedActivity.supportCardStatus = 'confirmed';
        state.selectedActivity.deliveryConfirmedOn = new Date().toISOString();
      }
    },
    completeTestStep: (state) => {
      if (!state.selectedActivity) return;
      const feedbackSurvey = findSurvey(state.selectedActivity);
      if (!feedbackSurvey?.startTime) return;

      if (!isAfterCurrentDate(feedbackSurvey.startTime))
        state.selectedActivity.stepStatus = 2;
    },
    resetSelectedActivity: (state) => {
      state.selectedActivity = undefined;
      state.supportRequest = undefined;
    },
    reset: () => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getIhutAvailableActivities.pending, (state) => {
        state.loading = true;
      })
      .addCase(getIhutAvailableActivities.fulfilled, (state, action) => {
        state.loading = false;
        state.availableActivities = mapApiIhutActivitiesToUiIhutActivities(
          action.payload
        );
      })
      .addCase(getIhutAvailableActivities.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getIhutProjectDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getIhutProjectDetails.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getIhutProjectDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedActivity = mapApiIhutDetailsToUiIhutDetails(
          action.payload
        );
      })
      .addCase(getIhutSupportRequest.pending, (state) => {
        state.supportLoading = true;
      })
      .addCase(getIhutSupportRequest.fulfilled, (state, action) => {
        state.supportLoading = false;
        const supportDataContent = action.payload.content;
        const supportData = supportDataContent[supportDataContent.length - 1];
        state.supportRequest = {
          createdAt: supportData?.createTime,
          updatedAt: supportData?.updateTime
        };
      })
      .addCase(getIhutSupportRequest.rejected, (state) => {
        state.supportLoading = false;
      })
      .addCase(updatePanelistState.pending, (state) => {
        state.updateStateLoading = true;
      })
      .addCase(updatePanelistState.fulfilled, (state, action) => {
        state.updateStateLoading = false;
        if (!state.selectedActivity) return;

        const status = action.meta.arg;
        if (status.delivery) {
          state.selectedActivity.shippingDetails.received = true;
          state.selectedActivity.stepStatus = 1;
        }

        if (status.testing) state.selectedActivity.testingCompleted = true;
      })
      .addCase(updatePanelistState.rejected, (state) => {
        state.updateStateLoading = false;
      });
  }
});

export const ihutActions = {
  ...ihutSlice.actions,
  getIhutAvailableActivities,
  getIhutProjectDetails,
  getIhutSupportRequest,
  updatePanelistState
};

const selectAvailableActivities = (state: ApplicationState) =>
  state.ihut.availableActivities;

const selectSelectedActivity = (state: ApplicationState) =>
  state.ihut.selectedActivity;

const selectLoading = (state: ApplicationState) => state.ihut.loading;

const selectSupportRequest = (state: ApplicationState) =>
  state.ihut.supportRequest;

const selectSupportLoading = (state: ApplicationState) =>
  state.ihut.supportLoading;

const selectUpdateStateLoading = (state: ApplicationState) =>
  state.ihut.updateStateLoading;

export const ihutSelectors = {
  selectAvailableActivities,
  selectSelectedActivity,
  selectLoading,
  selectSupportRequest,
  selectSupportLoading,
  selectUpdateStateLoading
};

export default ihutSlice.reducer;
